import { useEffect, useState } from 'react';
import type { TramoControlDTO } from '@ausentia/shared';
import { api, ErrorApi } from '../api/cliente.js';
import { useToast } from '../componentes/Toast.js';

/**
 * Administración de "tramos de control": el Operador define sus propias
 * modalidades/distancias (antes un enum fijo de 3 valores) y cada una tiene
 * su propia tarifa por empresa en la pantalla de Tarifas. Desactivar un tramo
 * no borra el historial (ausencias/tarifas ya cargadas siguen referenciándolo).
 */
export function Tramos() {
  const [tramos, setTramos] = useState<TramoControlDTO[] | null>(null);
  const [error, setError] = useState('');
  const toast = useToast();

  const [nombreNuevo, setNombreNuevo] = useState('');
  const [creando, setCreando] = useState(false);
  const [errorCrear, setErrorCrear] = useState('');

  async function refrescar() {
    try {
      setTramos(await api.get<TramoControlDTO[]>('/api/operador/tramos'));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudieron cargar los tramos de control');
    }
  }

  useEffect(() => {
    void refrescar();
  }, []);

  async function crear(e: React.FormEvent) {
    e.preventDefault();
    if (!nombreNuevo.trim()) {
      setErrorCrear('El nombre es obligatorio.');
      return;
    }
    setCreando(true);
    setErrorCrear('');
    try {
      await api.post('/api/operador/tramos', { nombre: nombreNuevo.trim() });
      toast.mostrar(`Tramo "${nombreNuevo.trim()}" creado.`);
      setNombreNuevo('');
      await refrescar();
    } catch (err) {
      setErrorCrear(err instanceof ErrorApi ? err.message : 'No se pudo crear el tramo');
    } finally {
      setCreando(false);
    }
  }

  return (
    <section>
      <h2>Tramos de control</h2>
      <p className="muted">
        Los tipos de visita (modalidad + distancia) que usás para cotizar y registrar controles. Cada tramo tiene su
        propia tarifa por empresa en la pantalla de Tarifas — no requiere cambios de código.
      </p>
      {error && <div className="error-box" role="status">{error}</div>}

      <form onSubmit={crear} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '16px 0 24px' }}>
        <div className="campo" style={{ flex: 1, marginBottom: 0 }}>
          <label htmlFor="nombreTramo">Nuevo tramo</label>
          <input
            id="nombreTramo"
            value={nombreNuevo}
            onChange={(e) => setNombreNuevo(e.target.value)}
            placeholder="Ej. Domicilio — 30 a 60 km"
          />
          {errorCrear && <p className="mini" style={{ color: 'var(--rojo)' }}>{errorCrear}</p>}
        </div>
        <button type="submit" className="secundario" style={{ width: 'auto', marginTop: 22 }} disabled={creando}>
          {creando ? 'Creando…' : 'Agregar tramo'}
        </button>
      </form>

      {tramos === null ? (
        <div className="cargando">Cargando…</div>
      ) : (
        <table className="tabla">
          <thead>
            <tr>
              <th scope="col">Orden</th>
              <th scope="col">Nombre</th>
              <th scope="col">Estado</th>
              <th scope="col" aria-label="Acciones" />
            </tr>
          </thead>
          <tbody>
            {tramos.map((t) => (
              <FilaTramo key={t.id} tramo={t} onGuardado={refrescar} />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function FilaTramo({ tramo, onGuardado }: { tramo: TramoControlDTO; onGuardado: () => Promise<void> }) {
  const toast = useToast();
  const [editando, setEditando] = useState(false);
  const [nombre, setNombre] = useState(tramo.nombre);
  const [orden, setOrden] = useState(String(tramo.orden));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [errorToggle, setErrorToggle] = useState('');

  async function guardar() {
    if (!nombre.trim()) {
      setError('El nombre es obligatorio.');
      return;
    }
    setGuardando(true);
    setError('');
    try {
      await api.put(`/api/operador/tramos/${tramo.id}`, {
        nombre: nombre.trim(),
        orden: Number(orden),
      });
      setEditando(false);
      await onGuardado();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  }

  async function toggleActivo() {
    setErrorToggle('');
    try {
      await api.post(`/api/operador/tramos/${tramo.id}/toggle-activo`, {});
      toast.mostrar(tramo.activo ? `Tramo "${tramo.nombre}" desactivado.` : `Tramo "${tramo.nombre}" activado.`);
      await onGuardado();
    } catch (err) {
      setErrorToggle(err instanceof ErrorApi ? err.message : 'No se pudo cambiar el estado');
    }
  }

  if (editando) {
    return (
      <tr>
        <td>
          <input
            type="number"
            value={orden}
            onChange={(e) => setOrden(e.target.value)}
            style={{ width: 70 }}
            aria-label={`Orden de ${tramo.nombre}`}
          />
        </td>
        <td>
          <input
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            aria-label="Nombre del tramo"
          />
          {error && <p className="mini" style={{ color: 'var(--rojo)' }}>{error}</p>}
        </td>
        <td>
          <span className={`insignia ${tramo.activo ? 'verde' : 'gris'}`}>{tramo.activo ? 'Activo' : 'Inactivo'}</span>
        </td>
        <td style={{ display: 'flex', gap: 6 }}>
          <button className="secundario" style={{ width: 'auto' }} onClick={guardar} disabled={guardando}>
            {guardando ? '…' : 'Guardar'}
          </button>
          <button
            className="fantasma"
            style={{ width: 'auto' }}
            onClick={() => {
              setEditando(false);
              setNombre(tramo.nombre);
              setOrden(String(tramo.orden));
              setError('');
            }}
            disabled={guardando}
          >
            Cancelar
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>{tramo.orden}</td>
      <td>{tramo.nombre}</td>
      <td>
        <span className={`insignia ${tramo.activo ? 'verde' : 'gris'}`}>{tramo.activo ? 'Activo' : 'Inactivo'}</span>
      </td>
      <td>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="secundario" style={{ width: 'auto' }} onClick={() => setEditando(true)}>
            Editar
          </button>
          <button className="fantasma" style={{ width: 'auto' }} onClick={toggleActivo}>
            {tramo.activo ? 'Desactivar' : 'Activar'}
          </button>
        </div>
        {errorToggle && <p className="mini" style={{ color: 'var(--rojo)' }}>{errorToggle}</p>}
      </td>
    </tr>
  );
}
